/*
 * <cart-note>
 * Persists the cart note to the cart via AJAX (cart/update.js) on input ("change" only triggers once),
 * debounced. Used on the cart page and in the mini-cart drawer, so it lives
 * in a shared asset rather than inline in a single section.
 *
 * Also flushed on focusout, undebounced: the note is server-rendered and several
 * components swap the markup around it wholesale, so a re-render mid-debounce
 * would put the stale note back. Blur fires on mousedown, ahead of the click
 * that triggers the re-render. Not `change`, which skips paste and autofill.
 */
if (!customElements.get("cart-note")) {
  // Mini-cart only: whether the note dropdown is expanded. Module scope, because
  // the footer re-render swaps the element out wholesale and the replacement would
  // otherwise come back collapsed after every quantity change, coupon, or removal.
  let expanded = false;

  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.lastSaved = null;
        this.debouncedSave = debounce(() => this.save(), 500);

        this.addEventListener("input", () => this.debouncedSave());
        this.addEventListener("focusout", () => this.save());
      }

      connectedCallback() {
        // Absent on the cart page, which renders the field without a dropdown.
        this.details = this.querySelector("details");

        if (!this.details) {
          return;
        }

        this.details.open = expanded;
        this.details.addEventListener("toggle", () => (expanded = this.details.open));
      }

      save() {
        const textarea = this.querySelector("textarea");

        if (!textarea) {
          return;
        }

        // defaultValue is the server-rendered note. Skips the unedited field,
        // and the debounced call a flush already beat to it.
        const persisted = this.lastSaved ?? textarea.defaultValue;

        if (textarea.value === persisted) {
          return;
        }

        this.lastSaved = textarea.value;

        const body = JSON.stringify({ note: textarea.value });

        // Outlives the navigation when the blur came from clicking a link.
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body }, keepalive: true });
      }
    },
  );
}
